import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./DoctorProfile.css";
import StarIcon from "../Setting/SettingsAssets/Star.svg";
import LineIcon from "../Setting/SettingsAssets/Line 25.svg";
import PlusIcon from "./SettingsAssets/Plus.svg";
import AddSpecialization from "./Popup/AddSpecialization";
import { Link } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useAuth } from "../../AuthProvider";
import AddEducation from "./Popup/AddEducation";
import Avatar from '@mui/material/Avatar';
import { useApp } from "../../AppProvider";
import api from "./../../axiosInstance";
import logger from "../../utils/logger";

// ======= Star Component - Memoized and moved outside ======= //
const Star = React.memo(({ fillPercent = 0 }) => {
  const clipId = `clip-${fillPercent}-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <svg width="34" height="32" viewBox="0 0 34 32">
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={`${fillPercent}%`} height="100%" />
        </clipPath>
      </defs>
      <path d="M17.8885 0.701385L21.6478 9.2758C21.7892 9.59849 22.0146 9.87731 22.3006 10.0832C22.5865 10.289 22.9224 10.4143 23.2733 10.4461L32.4314 11.2672C33.4676 11.418 33.881 12.6888 33.1297 13.4206L26.2311 19.216C25.6725 19.6852 25.4183 20.4225 25.5719 21.1347L27.5773 30.5219C27.7532 31.5525 26.6723 32.3401 25.7451 31.8514L17.7516 27.1704C17.4501 26.9934 17.1069 26.9001 16.7573 26.9001C16.4078 26.9001 16.0645 26.9934 15.763 27.1704L7.76957 31.8486C6.8451 32.3346 5.76143 31.5497 5.93739 30.5191L7.94273 21.132C8.09355 20.4197 7.84219 19.6824 7.2836 19.2132L0.382185 13.4234C-0.366329 12.6944 0.0470291 11.4208 1.08043 11.27L10.2386 10.4488C10.5894 10.4171 10.9254 10.2918 11.2113 10.086C11.4972 9.88011 11.7226 9.60128 11.8641 9.27859L15.6234 0.704178C16.0898 -0.23426 17.4248 -0.23426 17.8885 0.701385Z" fill="#E0E0E0" />
      <path clipPath={`url(#${clipId})`} d="M17.8885 0.701385L21.6478 9.2758C21.7892 9.59849 22.0146 9.87731 22.3006 10.0832C22.5865 10.289 22.9224 10.4143 23.2733 10.4461L32.4314 11.2672C33.4676 11.418 33.881 12.6888 33.1297 13.4206L26.2311 19.216C25.6725 19.6852 25.4183 20.4225 25.5719 21.1347L27.5773 30.5219C27.7532 31.5525 26.6723 32.3401 25.7451 31.8514L17.7516 27.1704C17.4501 26.9934 17.1069 26.9001 16.7573 26.9001C16.4078 26.9001 16.0645 26.9934 15.763 27.1704L7.76957 31.8486C6.8451 32.3346 5.76143 31.5497 5.93739 30.5191L7.94273 21.132C8.09355 20.4197 7.84219 19.6824 7.2836 19.2132L0.382185 13.4234C-0.366329 12.6944 0.0470291 11.4208 1.08043 11.27L10.2386 10.4488C10.5894 10.4171 10.9254 10.2918 11.2113 10.086C11.4972 9.88011 11.7226 9.60128 11.8641 9.27859L15.6234 0.704178C16.0898 -0.23426 17.4248 -0.23426 17.8885 0.701385Z" fill="#FDD835" />
    </svg>
  );
});

Star.displayName = 'Star';

const DoctorProfile = () => {
  const { user, setUser } = useAuth();
  const { stringAvatar, stringToColor } = useApp();

  // ======= Memoized initial data ======= //
  const initialData = useMemo(() => ({
    ...user,
    // specializations: user?.specializations || [],
    specilizations: user?.specilizations || [],
    education: user?.education || [],
    addresses: user?.addresses || [],
    about: user?.about || "",
    yearsOfExperience: user?.yearsOfExperience || "",
    numberOfPatientsChecked: user?.numberOfPatientsChecked || "",
  }), [user]);

  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(() => {
    const storedData = localStorage.getItem("doctorProfile");
    return storedData ? JSON.parse(storedData) : initialData;
  });
  const [openAddSpecialization, setOpenAddSpecialization] = useState(false);
  const [openAddEducation, setOpenAddEducation] = useState(false);

  logger.debug('DOCTOR PROFILE', user.specilizations)

  // ======= Debounced API call for updating profile ======= //
  const fetchDoctorProfile = useCallback(async (doctorId, updatedProfile) => {
    try {
      const response = await api.put(`/doctors/${doctorId}`, updatedProfile);
      const data = response.data;
      setUser(data);
      setEditedData(data);
      localStorage.setItem("doctorProfile", JSON.stringify(data));
    } catch (error) {
      logger.error("Error updating doctor profile data:", error.message);
    }
  }, [setUser]);

  // ======= Only fetch on mount, not on every user change ======= //
  // useEffect(() => {
  //   if (user) {
  //     fetchDoctorProfile(user.id, initialData);
  //   }
  // }, []); // Empty dependency array - only run once on mount

  useEffect(() => {
    if (user) {
      setEditedData({
        ...user,
        specilizations: user.specilizations || [],
        education: user.education || [],
        addresses: user.addresses || [],
      });
    }
  }, [user]);


  const handleEditButtonClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  // ======= Handle Save Button Click ======= //
  const handleSaveButtonClick = useCallback(async () => {
    setIsEditing(false);
    if (user?._id) {
      await fetchDoctorProfile(user._id, editedData);
    }
  }, [user?._id, editedData, fetchDoctorProfile]);

  // ======= Debounced input change - removed immediate API call ======= //
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditedData(prev => {
      const updated = { ...prev, [name]: value };
      // localStorage.setItem("doctorProfile", JSON.stringify(updated));
      return updated;
    });
  }, []);

  // ======= Address Handlers - optimized ======= //
  const handleClinicChange = useCallback((e, index) => {
    const { value } = e.target;
    setEditedData(prev => {
      const updatedAddresses = [...prev.addresses];
      updatedAddresses[index] = { ...updatedAddresses[index], clinic: value };
      const updated = { ...prev, addresses: updatedAddresses };
      // localStorage.setItem("doctorProfile", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const handleAddressTextChange = useCallback((e, index) => {
    const { value } = e.target;
    setEditedData(prev => {
      const updatedAddresses = [...prev.addresses];
      updatedAddresses[index] = { ...updatedAddresses[index], address: value };
      const updated = { ...prev, addresses: updatedAddresses };
      // localStorage.setItem("doctorProfile", JSON.stringify(updated));
      return updated;
    });
  }, []);

  // ======= Add Specialization Handler ======= //
  const handleAddSpecialization = useCallback(async (specilizations) => {
    const updatedProfile = { ...editedData, specilizations };
    setEditedData(updatedProfile);
    if (user?._id) {
      await fetchDoctorProfile(user._id, updatedProfile);
    }
  }, [editedData, user?._id, fetchDoctorProfile]);

  // ======= Add Education Handler ======= //
  const handleAddEducation = useCallback(async (education) => {
    const updatedProfile = { ...editedData, education };
    setEditedData(updatedProfile);
    if (user?._id) {
      await fetchDoctorProfile(user._id, updatedProfile);
    }
  }, [editedData, user?._id, fetchDoctorProfile]);

  // ======= Memoized calculations ======= //
  const { averageRating, totalReviews } = useMemo(() => {
    const reviews = user?.reviews || [];
    const avg = reviews.length > 0
      ? reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length
      : 0;
    return { averageRating: avg, totalReviews: reviews.length };
  }, [user?.reviews]);

  // ======= Memoized star rating display ======= //
  const starRating = useMemo(() => {
    return [...Array(5)].map((_, index) => {
      const starNumber = index + 1;
      let fillPercent = 0;

      if (averageRating >= starNumber) {
        fillPercent = 100;
      } else if (averageRating >= starNumber - 0.5) {
        fillPercent = 50;
      }

      return <Star key={index} fillPercent={fillPercent} />;
    });
  }, [averageRating]);

  // ======= Memoized doctor name ======= //
  const doctorName = useMemo(() =>
    `Dr. ${user?.name?.firstName || ""} ${user?.name?.middleName || ""} ${user?.name?.lastName || ""}`.trim(),
    [user?.name]
  );

  // ======= Memoized avatar props ======= //
  const avatarProps = useMemo(() => ({
    sx: {
      width: { md: 90, lg: 100, xl: 120 },
      height: { md: 90, lg: 100, xl: 120 },
      bgcolor: user?.img ? 'transparent' : stringToColor(user?.name?.firstName || 'D')
    },
    alt: user?.name?.firstName || 'Doctor',
    src: user?.img
  }), [user?.img, user?.name?.firstName, stringToColor]);

  return (
    <div className="doctor_profile_container">
      <Link className="backLink" to="/setting">
        <div className="back-link-button">
          <ArrowBackIosIcon />Go Back
        </div>
      </Link>

      <div className="doctor_profile_section">
        <div className="doctor_top_container">
          <Avatar {...avatarProps}>
            {!user?.img && stringAvatar(`${user?.name?.firstName || 'D'}`).children}
          </Avatar>

          <div className="doctor_info_container">
            <h3 className="doctor_heading">{doctorName}</h3>
            <p className="doctor_role">{editedData.specilizations?.join(", ")}</p>

            <div className="star_icon_container">
              {starRating}
              <p className="reviews">{averageRating.toFixed(1)} reviews</p>
            </div>
          </div>
        </div>

        {/* Doctor Mid Section */}
        <div className="doctor_mid_section">
          {/* SAVE & EDIT BUTTON */}
          <div className="doc_edit_buttonbox">
            {isEditing ? (
              <button className="doc_save_button" onClick={handleSaveButtonClick}>
                Save
              </button>
            ) : (
              <button className="doc_edit_button" onClick={handleEditButtonClick}>
                Edit
              </button>
            )}
          </div>

          <div className="doctor_left_container">
            <div className="doctor_intro_container">
              {/* INTRODUCTION AREA */}
              <div className="doctor_intro">
                <h3 className="doctor_heading">Introduction</h3>
                <p className="doctor_intro_content">
                  {isEditing ? (
                    <textarea
                      className="doc_intro_textarea"
                      name="about"
                      value={editedData.about || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    editedData.about || ""
                  )}
                </p>
              </div>

              {/* SPECIALIZATION SECTION */}
              <div className="doctor_specialization">
                <img
                  className="doc_plus_icon"
                  src={PlusIcon}
                  alt="Plus Icon"
                  onClick={() => setOpenAddSpecialization(true)}
                />
                <h3 className="doctor_heading">Specialization</h3>
                <div className="doctor_specialization_content">
                  {editedData.specilizations?.length === 0 ? (
                    <div className="add_specialization_text">Add Specialization</div>
                  ) : (
                    <ul className="doctor_spec_lists">
                      {editedData.specilizations?.map((spec, index) => (
                        <li key={index} className="doctor_spec_list">
                          {spec}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* EDUCATION SECTION */}
              <div className="doctor_education">
                <img
                  className="doc_plus_icon"
                  src={PlusIcon}
                  alt="Plus Icon"
                  onClick={() => setOpenAddEducation(true)}
                />
                <h3 className="doctor_heading">Education</h3>
                <div className="doctor_education_content">
                  <ul className="doctor_edu_lists">
                    {editedData.education?.map((edu, index) => {
                      const year = new Date(edu.year).getFullYear();
                      return (
                        <li key={index} className="doctor_edu_list">
                          {edu.degree} <span>{year}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section */}
          <div className="doctor_right_container">
            <div className="doctor_experience_box">
              <div className="doctor_exp_content_box">
                <div className="doctor_exp_row">
                  <p className="doctor_text">Years of Experience</p>
                  <p className="doctor_exp_text">
                    {isEditing ? (
                      <input
                        className="doctor_exp_text_input"
                        type="text"
                        name="yearsOfExperience"
                        value={editedData.yearsOfExperience || ""}
                        onChange={handleChange}
                      />
                    ) : (
                      editedData.yearsOfExperience || "N/A"
                    )}
                  </p>
                </div>
                <div className="doctor_exp_row">
                  <p className="doctor_text">Patients Checked</p>
                  <p className="doctor_exp_text">
                    {isEditing ? (
                      <input
                        className="doctor_exp_text_input"
                        type="text"
                        name="numberOfPatientsChecked"
                        value={editedData.numberOfPatientsChecked || ""}
                        onChange={handleChange}
                      />
                    ) : (
                      editedData.numberOfPatientsChecked || "N/A"
                    )}
                  </p>
                </div>
                <div className="doctor_exp_row">
                  <p className="doctor_text">Registration No.</p>
                  <p className="doctor_exp_text">
                    {isEditing ? (
                      <input
                        className="doctor_exp_text_input"
                        type="text"
                        name="registrationNumber"
                        value={editedData.registrationNumber || ""}
                        onChange={handleChange}
                        placeholder="e.g. MH-12345"
                      />
                    ) : (
                      editedData.registrationNumber || "N/A"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* <div className="doctor_address_box">
              <h3 className="doctor_heading">Address</h3>
              {editedData?.addresses?.map((address, index) => (
                <div key={index} className="doctor_address_content">
                  <div className="doctor_address_heading">
                    {isEditing ? (
                      <input
                        className="doctor_clinic_input"
                        type="text"
                        value={address.clinic || ""}
                        onChange={(e) => handleClinicChange(e, index)}
                      />
                    ) : (
                      address.clinic || "N/A"
                    )}
                  </div>
                  <div className="doctor_address_text">
                    {isEditing ? (
                      <input
                        className="doctor_address_text_input"
                        type="text"
                        value={address.address || ""}
                        onChange={(e) => handleAddressTextChange(e, index)}
                      />
                    ) : (
                      address.address || "N/A"
                    )}
                  </div>
                </div>
              ))}
            </div> */}
          </div>
        </div>

        {/* Review Section */}
        {/* <div className="review_container">
          <div className="review_content">
            <h3 className="doctor_heading">Reviews</h3>
            <div className="review_card_container">
              {user?.reviews?.map((review, index) => (
                <div key={index} className="review_card_box">
                  <div className="review_cards">
                    <div className="review_card_text">{review.reviewText}</div>
                    <div className="reviewver_box">
                      <img src={LineIcon} alt="Line Icon" />
                      <div className="reviewver">{review.reviewer}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div> */}
      </div>

      <AddSpecialization
        fetchDoctorProfile={fetchDoctorProfile}
        open={openAddSpecialization}
        onClose={() => setOpenAddSpecialization(false)}
        addData={handleAddSpecialization}
        data={editedData.specilizations}
        type="specialization"
        placeholder="Enter your area of expertise/specialization"
        user={user}
        editedData={editedData}
      />

      <AddEducation
        fetchDoctorProfile={fetchDoctorProfile}
        open={openAddEducation}
        onClose={() => setOpenAddEducation(false)}
        addData={handleAddEducation}
        data={editedData.education}
        type="education"
        placeholder="Enter your education details"
        user={user}
        editedData={editedData}
      />
    </div>
  );
};

export default DoctorProfile;